import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";

import { cn } from "~/lib/utils";
import { ScrollArea, ScrollBar } from "./scroll-area";

type TabsVariant = "default" | "dialog-header" | "form-section";

const TabsVariantContext = React.createContext<TabsVariant>("default");

function Tabs({
  className,
  variant = "default",
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root> & {
  variant?: TabsVariant;
}) {
  return (
    <TabsVariantContext.Provider value={variant}>
      <TabsPrimitive.Root
        data-slot="tabs"
        data-variant={variant}
        className={cn(
          "flex flex-col gap-2",
          variant === "dialog-header" ? "-m-6 pb-6 pt-0" : "",
          variant === "form-section" ? "gap-0" : "",
          className,
        )}
        {...props}
      />
    </TabsVariantContext.Provider>
  );
}

function TabsList({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List>) {
  const variant = React.useContext(TabsVariantContext);

  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      className={cn(
        variant === "dialog-header" && "px-6",
        variant === "dialog-header" &&
          "flex h-14 border-b-2 border-neutral-darkest/30",
        variant === "form-section" && "flex gap-4 mb-6",
        className,
      )}
      {...props}
    />
  );
}

function TabsTrigger({
  className,
  children,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  const variant = React.useContext(TabsVariantContext);

  return (
    <TabsPrimitive.Trigger
      {...props}
      className={cn(
        "cursor-pointer relative inline-flex items-center justify-center gap-1.5 rounded-md border border-transparent px-3 py-1.5 text-sm font-medium whitespace-nowrap text-dove-gray-dark transition-[color,box-shadow] focus-visible:ring-[3px] focus-visible:outline-1 disabled:pointer-events-none disabled:opacity-50",
        "after:content-[''] after:pointer-events-none after:absolute after:-bottom-1 after:left-1/2 after:h-1 after:w-full after:-translate-x-1/2 after:rounded-full after:bg-dove-gray-dark after:opacity-0 after:transition-opacity after:duration-200 data-[state=active]:after:opacity-100",
        variant === "form-section" &&
          "py-3 px-5 bg-gray-lightest after:hidden rounded-md text-black data-[state=active]:bg-dove-gray-dark data-[state=active]:text-white data-[state=active]:scale-105 data-[state=active]:border-black border-2 border-gray-lightest not-first:-ml-px transition-transform",
        variant === "form-section" && "data-[has-error='true']:border-red-500!",
        className,
      )}
    >
      {children}
    </TabsPrimitive.Trigger>
  );
}

function TabsContent({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
  const variant = React.useContext(TabsVariantContext);

  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn(
        "flex-1 outline-none",
        variant === "dialog-header" && "px-6",
        variant === "form-section" && "",
        className,
      )}
      {...props}
    />
  );
}

function TabsScrollArea({
  className,
  children,
  ...props
}: React.ComponentProps<typeof ScrollArea>) {
  return (
    <ScrollArea
      data-slot="tabs-scroll-area"
      className={cn("h-[70vh] -mx-6 mt-2 px-6", className)}
      {...props}
    >
      {children}
    </ScrollArea>
  );
}

type FormSectionTab = {
  value: string;
  label: React.ReactNode;
  content: React.ReactNode;
  disabled?: boolean;
  hasError?: boolean;
};

function FormSectionTabs({
  tabs,
  defaultValue,
  className,
  listClassName,
  triggerClassName,
  contentClassName,
  ...props
}: Omit<React.ComponentProps<typeof TabsPrimitive.Root>, "children"> & {
  tabs: FormSectionTab[];
  listClassName?: string;
  triggerClassName?: string;
  contentClassName?: string;
}) {
  const firstEnabled =
    tabs.find((t) => !t.disabled)?.value ?? tabs.at(0)?.value ?? "";

  return (
    <Tabs
      variant="form-section"
      defaultValue={defaultValue ?? firstEnabled}
      className={cn("w-full", className)}
      {...props}
    >
      <TabsList className={cn("flex flex-wrap", listClassName)}>
        {tabs.map((tab) => (
          <TabsTrigger
            key={tab.value}
            value={tab.value}
            disabled={tab.disabled}
            data-has-error={tab.hasError}
            className={triggerClassName}
          >
            {tab.label}
          </TabsTrigger>
        ))}
      </TabsList>
      {tabs.map((tab) => (
        <TabsContent
          key={tab.value}
          value={tab.value}
          className={contentClassName}
        >
          {tab.content}
        </TabsContent>
      ))}
    </Tabs>
  );
}

export {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  TabsScrollArea,
  FormSectionTabs,
};
